import { useEffect, useState } from 'react';
import { ApiError } from '../lib/api';
import { signatureApi, type MySignatureInfo } from '../lib/signatures';
import SignDialog from './SignDialog';

/**
 * "My signature" — every login draws theirs once (25 September 2026). It is
 * stamped, with their name and the time, on what they prepare, approve, pay or
 * check: the day report, the pay run pack, loan and advance receipts, the fund
 * payment, the statement. A new drawing is used from then on; documents from
 * before keep the signature they were signed with.
 */
export default function MySignature({ onClose }: { onClose: () => void }) {
  const [info, setInfo] = useState<MySignatureInfo | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    signatureApi.mine()
      .then(setInfo)
      .catch((err: unknown) => setError(err instanceof ApiError ? err.message : 'Could not reach the office.'));
  }, []);

  if (!info) {
    return (
      <div className="modal" role="dialog" aria-modal="true" aria-label="My signature">
        <div className="mbox">
          <div className="mhead">My signature</div>
          <div className="sub">{error || 'Loading…'}</div>
          <div className="bar" style={{ marginTop: 12 }}>
            <button type="button" className="btn" onClick={onClose}>Close</button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <SignDialog
      title="My signature"
      lead={
        info.saved
          ? 'This goes on everything you prepare or approve, with your name and the time. Sign again to change it — documents already signed keep the old one.'
          : 'Draw it once. It goes on everything you prepare or approve — day reports, pay runs, receipts — with your name and the time.'
      }
      padLabel="Your signature"
      // The version saved at savedAt: a fresh address each time, so a new
      // drawing is never shown from the browser's image cache.
      signedPath={info.saved ? signatureApi.userPath(info.userId, info.savedAt) : null}
      signedAt={info.savedAt}
      onSave={async (png) => setInfo(await signatureApi.saveMine(png))}
      onClose={onClose}
    />
  );
}
